import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { CompanyLogoImage } from 'components/common/CompanyLogo';
import ProfileNavigation from './ProfileNavigation';
import { EMPLOYEE_ROLES } from 'constants/user';
import { PROFILE } from 'helpers/routes';
import { signOut } from 'store/auth';
import {
  VERIFICATION_STATE,
  VERIFICATION_STATES,
  type VerificationState,
} from 'utils/documentVerification';

import styles from './CompanyProfile/styles.module.scss';

const verifiedIconSrc = new URL('../../../img/verified-blue.svg', import.meta.url).href;


interface ProfileCardLayoutProps {
  children: ReactNode;
}

const ProfileCardLayout = memo(({ children }: ProfileCardLayoutProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.profile.profileData);
  const companyData = useSelector((state: any) => state.company.companyData);
  const companyLogo = useSelector((state: any) => state.company.logo);
  const verificationData = useSelector((state: any) => state.verification.data);

  const canEdit = user.employee_company_roles === EMPLOYEE_ROLES.OWNER;

  // Статус верификации: заявку и решение модератора знает только /v2/profile/verification,
  // без него остаётся публичный флаг is_verified
  const apiVerificationStatus = verificationData?.status || companyData.verification_status;
  const verificationState: VerificationState =
    apiVerificationStatus && VERIFICATION_STATES.indexOf(apiVerificationStatus) !== -1
      ? apiVerificationStatus
      : companyData.is_verified
        ? VERIFICATION_STATE.VERIFIED
        : VERIFICATION_STATE.NONE;
  const isVerified = verificationState === VERIFICATION_STATE.VERIFIED;

  const signOutHandle = () => {
    dispatch(signOut());
    navigate('/', { replace: true });
  };

  return (
    <div className={styles.formContainer}>
      {/* Desktop Header */}
      <div className={styles.formHeader}>
        <span className={styles.formHeaderTitle}>{t('profile.profile_section.main_info')}</span>
        <div className={styles.headerActions}>
          <button className={styles.logoutButton} onClick={signOutHandle} type="button">
            {t('profile.profile_menu.log_out_button')}
          </button>
          {canEdit && (
            <NavLink className={styles.editButton} to={PROFILE.edit}>
              {t('profile.edit_button')}
            </NavLink>
          )}
        </div>
      </div>

      <div className={styles.formContent}>
        {/* Header Section with Logo and Company Info */}
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <CompanyLogoImage mode={companyLogo} className={companyLogo ? styles.logoImage : styles.logoPlaceholder} />
          </div>
          <div className={styles.companyInfo}>
            <div className={styles.companyNameRow}>
              <h2 className={styles.companyName}>{companyData.name}</h2>
              <span
                className={cn(styles.verificationBadge, styles[`verificationBadge--${verificationState}`])}
              >
                <img
                  src={verifiedIconSrc}
                  alt=""
                  className={cn(styles.verificationIcon, {
                    [styles.verificationIconColored]: isVerified,
                  })}
                />
                {!isVerified && t(`documents.verification.${verificationState}.title`)}
              </span>
            </div>
            {companyData.company_roles && (
              <span className={styles.roleBadge}>{companyData.company_roles}</span>
            )}
            {/* Mobile Edit Button */}
            {canEdit && (
              <NavLink className={`${styles.editButton} ${styles.mobileEditButton}`} to={PROFILE.edit}>
                {t('profile.edit_button')}
              </NavLink>
            )}
          </div>
        </div>

        {/* Tab Navigation - inside card on desktop per Figma design */}
        <div className="profileNavInner">
          <ProfileNavigation />
        </div>

        {/* Page content (varies per tab) */}
        {children}
      </div>
    </div>
  );
});

ProfileCardLayout.displayName = 'ProfileCardLayout';

export default ProfileCardLayout;
